import React, { useEffect, useState } from 'react'
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Skeleton,
  Switch,
  TextField,
  Typography,
  Avatar,
} from '@mui/material'
import { ArrowBack, CardMembership, Delete, Save } from '@mui/icons-material'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { api } from '../../../../services/Main/Api'

const slugify = (text) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/(^_|_$)/g, '')

const PlanForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const isEdit = !!id
  const [isLoading, setIsLoading] = useState(isEdit)
  const [submitting, setSubmitting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [allFeatures, setAllFeatures] = useState([])
  const [selectedFeatures, setSelectedFeatures] = useState([])

  const { control, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      slug: '',
      description: '',
      is_active: true,
      order: 0,
    },
  })

  const watchName = watch('name')

  useEffect(() => {
    if (!isEdit && watchName) {
      setValue('slug', slugify(watchName))
    }
  }, [watchName, isEdit, setValue])

  useEffect(() => {
    fetchFeatures()
    if (isEdit) fetchPlan()
  }, [id])

  const fetchFeatures = async () => {
    try {
      const res = await api.get('/plan/features/', { params: { page_size: 200, is_active: true } })
      setAllFeatures(res.data.results || [])
    } catch (err) {
      console.error(err)
    }
  }

  const fetchPlan = async () => {
    try {
      const res = await api.get(`/plan/plans/${id}/`)
      const data = res.data
      reset({
        name: data.name || '',
        slug: data.slug || '',
        description: data.description || '',
        is_active: data.is_active ?? true,
        order: data.order ?? 0,
      })
      const featureIds = (data.features || []).map(f =>
        typeof f === 'object' ? f.id : f
      ).filter(id => id != null)
      setSelectedFeatures(featureIds)
    } catch (err) {
      enqueueSnackbar('Erro ao carregar plano', { variant: 'error' })
      navigate('/admin/planos')
    } finally {
      setIsLoading(false)
    }
  }

  const buildFeatureTree = () => {
    const modules = allFeatures.filter(f => f.category === 'MODULE')
    const features = allFeatures.filter(f => f.category === 'FEATURE')
    const orphans = features.filter(f => !f.parent)
    const groups = modules.map(mod => ({
      module: mod,
      children: features.filter(f => f.parent === mod.id),
    }))
    return { groups, orphans }
  }

  const toggleFeature = (featureId) => {
    setSelectedFeatures(prev =>
      prev.includes(featureId)
        ? prev.filter(id => id !== featureId)
        : [...prev, featureId]
    )
  }

  const toggleModule = (moduleId, childIds) => {
    const allIds = [moduleId, ...childIds]
    const allSelected = allIds.every(id => selectedFeatures.includes(id))
    setSelectedFeatures(prev =>
      allSelected
        ? prev.filter(id => !allIds.includes(id))
        : [...new Set([...prev, ...allIds])]
    )
  }

  const isModuleIndeterminate = (moduleId, childIds) => {
    const allIds = [moduleId, ...childIds]
    const selectedCount = allIds.filter(id => selectedFeatures.includes(id)).length
    return selectedCount > 0 && selectedCount < allIds.length
  }

  const onSubmit = async (formData) => {
    try {
      setSubmitting(true)
      const payload = {
        ...formData,
        features: selectedFeatures.filter(id => id != null),
      }

      if (isEdit) {
        await api.put(`/plan/plans/${id}/`, payload)
        enqueueSnackbar('Plano atualizado com sucesso', { variant: 'success' })
      } else {
        await api.post('/plan/plans/', payload)
        enqueueSnackbar('Plano criado com sucesso', { variant: 'success' })
      }
      navigate('/admin/planos')
    } catch (err) {
      const errorMsg = err?.response?.data
      if (errorMsg && typeof errorMsg === 'object') {
        const firstError = Object.values(errorMsg).flat()[0]
        enqueueSnackbar(firstError || 'Erro ao salvar', { variant: 'error' })
      } else {
        enqueueSnackbar('Erro ao salvar plano', { variant: 'error' })
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    try {
      setSubmitting(true)
      await api.delete(`/plan/plans/${id}/`)
      enqueueSnackbar('Plano excluído com sucesso', { variant: 'success' })
      navigate('/admin/planos')
    } catch (err) {
      enqueueSnackbar('Erro ao excluir plano', { variant: 'error' })
    } finally {
      setSubmitting(false)
      setDeleteDialogOpen(false)
    }
  }

  const { groups, orphans } = buildFeatureTree()

  return (
    <>
      <Helmet>
        <title>{isEdit ? 'Editar' : 'Novo'} Plano - Admin</title>
      </Helmet>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={submitting}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/planos')}
          >
            Voltar
          </Button>
          <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
            {isEdit ? 'Editar' : 'Novo'} Plano
          </Typography>
        </Box>

        {isLoading ? (
          <Skeleton variant="rounded" height={600} animation="pulse" />
        ) : (
          <>
            <Card sx={{ mb: 3 }}>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <CardMembership />
                  </Avatar>
                }
                title={isEdit ? 'Editar Plano' : 'Novo Plano'}
              />
              <Divider />
              <CardContent>
                <form id="plan-form" onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={2} rowSpacing={3}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="name"
                        control={control}
                        rules={{ required: 'Nome é obrigatório' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Nome"
                            fullWidth
                            size="small"
                            required
                            error={!!errors.name}
                            helperText={errors.name?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="slug"
                        control={control}
                        rules={{ required: 'Slug é obrigatório' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Slug"
                            fullWidth
                            size="small"
                            required
                            error={!!errors.slug}
                            helperText={errors.slug?.message || 'Identificador único (auto-gerado)'}
                            InputProps={{
                              sx: { fontFamily: 'monospace' },
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Descrição"
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="order"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Ordem"
                            fullWidth
                            size="small"
                            type="number"
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="is_active"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                color="primary"
                              />
                            }
                            label="Ativo"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardHeader title="Funcionalidades do Plano" />
              <Divider />
              <CardContent>
                {allFeatures.length === 0 ? (
                  <Typography color="text.secondary">
                    Nenhuma funcionalidade disponível. Crie funcionalidades primeiro.
                  </Typography>
                ) : (
                  <Box>
                    {groups.map(({ module: mod, children }) => (
                      <Box key={mod.id} sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={[mod.id, ...children.map(c => c.id)].every(id => selectedFeatures.includes(id))}
                              indeterminate={isModuleIndeterminate(mod.id, children.map(c => c.id))}
                              onChange={() => toggleModule(mod.id, children.map(c => c.id))}
                              color="primary"
                            />
                          }
                          label={
                            <Typography fontWeight="bold">
                              {mod.name}
                            </Typography>
                          }
                        />
                        {children.length > 0 && (
                          <Box sx={{ pl: 4 }}>
                            {children.map((child) => (
                              <FormControlLabel
                                key={child.id}
                                control={
                                  <Checkbox
                                    checked={selectedFeatures.includes(child.id)}
                                    onChange={() => toggleFeature(child.id)}
                                    size="small"
                                  />
                                }
                                label={child.name}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))}

                    {orphans.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Outros
                        </Typography>
                        <Box sx={{ pl: 1 }}>
                          {orphans.map((feature) => (
                            <FormControlLabel
                              key={feature.id}
                              control={
                                <Checkbox
                                  checked={selectedFeatures.includes(feature.id)}
                                  onChange={() => toggleFeature(feature.id)}
                                  size="small"
                                />
                              }
                              label={feature.name}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {selectedFeatures.length} funcionalidade(s) selecionada(s)
                </Typography>
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
              {isEdit ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Excluir
                </Button>
              ) : (
                <Box />
              )}
              <Button
                type="submit"
                form="plan-form"
                variant="contained"
                startIcon={<Save />}
                disabled={submitting}
              >
                {isEdit ? 'Salvar' : 'Criar'}
              </Button>
            </Box>
          </>
        )}

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirmar exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir este plano? Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  )
}

export default PlanForm
